import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import axios from 'axios';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [projectCarouselIndexes, setProjectCarouselIndexes] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects
      .filter((p) => selectedCategory === 'all' || p.category === selectedCategory)
      .filter((p) => {
        if (!q) return true;
        return (
          (p.title || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q) ||
          (p.location || '').toLowerCase().includes(q)
        );
      });
  }, [projects, selectedCategory, query]);

  const categories = [
    { value: 'all', label: 'All Projects' },
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const getProjectImages = (project) => {
    // Return array of images (supports both single image and multiple images)
    if (project.images && project.images.length > 0) {
      return project.images;
    }
    return project.image ? [project.image] : [];
  };

  const getCurrentImage = (project) => {
    const images = getProjectImages(project);
    const currentIndex = projectCarouselIndexes[project.id] || 0;
    return images[currentIndex] || images[0];
  };

  const getCurrentOverlay = (project) => {
    const overlays = project.imageOverlays || [];
    const currentIndex = projectCarouselIndexes[project.id] || 0;
    return overlays[currentIndex] || '';
  };

  const handleCarouselPrev = (project, e) => {
    e.stopPropagation();
    const images = getProjectImages(project);
    const currentIndex = projectCarouselIndexes[project.id] || 0;
    const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    setProjectCarouselIndexes(prev => ({ ...prev, [project.id]: newIndex }));
  };

  const handleCarouselNext = (project, e) => {
    e.stopPropagation();
    const images = getProjectImages(project);
    const currentIndex = projectCarouselIndexes[project.id] || 0;
    const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    setProjectCarouselIndexes(prev => ({ ...prev, [project.id]: newIndex }));
  };

  const openProjectDetail = (project) => {
    navigate(`/gallery/${project.id}`);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Project <span style={{ color: '#00CED1' }}>Gallery</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Explore our portfolio of exceptional architectural projects spanning residential and commercial spaces
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 px-4 bg-white border-b">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category.value}
                variant={selectedCategory === category.value ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.value)}
                className={`font-semibold transition-all duration-300 ${
                  selectedCategory === category.value 
                    ? 'text-white' 
                    : 'hover:scale-105'
                }`}
                style={
                  selectedCategory === category.value 
                    ? { backgroundColor: '#00CED1' }
                    : { borderColor: '#00CED1', color: '#00CED1' }
                }
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
            {filteredProjects.map((project, index) => {
              const images = getProjectImages(project);
              const hasMultipleImages = images.length > 1;
              const currentImage = getCurrentImage(project);
              const currentImageIndex = projectCarouselIndexes[project.id] || 0;
              
              return (
                <div 
                  key={project.id}
                  className="break-inside-avoid mb-6 group cursor-pointer"
                  onClick={() => openProjectDetail(project)}
                  data-testid={`gallery-project-${project.id}`}
                >
                  <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 bg-gray-100">
                    <img 
                      src={cldPresets.galleryCard(currentImage.startsWith('http') ? currentImage : `${BACKEND_URL}${currentImage}`)}
                      alt={project.title}
                      className="block w-full h-auto object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />

                    {/* Per-image overlay caption (always visible if set) */}
                    {getCurrentOverlay(project) && (
                      <div className="absolute inset-x-0 bottom-0 px-5 py-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none">
                        <p className="text-white font-semibold text-sm md:text-base drop-shadow-lg" data-testid={`gallery-overlay-${project.id}`}>
                          {getCurrentOverlay(project)}
                        </p>
                      </div>
                    )}
                    
                    {/* Carousel Controls */}
                    {hasMultipleImages && (
                      <>
                        <button
                          onClick={(e) => handleCarouselPrev(project, e)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => handleCarouselNext(project, e)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                        
                        {/* Image Counter */}
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          {currentImageIndex + 1} / {images.length}
                        </div>
                      </>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <div className="flex items-center justify-between mb-3">
                          <Badge 
                            className="text-white capitalize"
                            style={{ backgroundColor: project.category === 'commercial' ? '#FF0000' : '#00CED1' }}
                          >
                            {project.category}
                          </Badge>
                          <div className="flex items-center space-x-2 text-sm">
                            {project.year && (
                              <span className="text-gray-300">{project.year}</span>
                            )}
                          </div>
                        </div>
                        <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                        <p className="text-sm text-gray-300 mb-2">{project.description}</p>
                        {project.location && (
                          <p className="text-xs text-gray-400">{project.location}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Want to See Your Project Here?
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Let's collaborate to create something extraordinary that showcases your vision
          </p>
          <Button 
            size="lg"
            className="text-white font-semibold px-8 py-6 text-base hover:scale-105 transition-transform duration-300"
            style={{ backgroundColor: '#00CED1' }}
            onClick={() => window.location.href = '/contact'}
          >
            Start Your Project
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
